'use client';
import styled from 'styled-components';
import { BodyTextMediumA, HeadingBoldH5 } from '@/shared/ui/typography2';
import { fetcherWithToken } from '@/shared/api/fetcher';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import useIsMobile from '@/shared/hooks/useIsMobile';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useScopedI18n } from '@locales/client';
import { useGameApi } from '@/entities/game/model/api';
import { WinItem } from '@/widgets/record-wins/WinItem';

export const RecordWins = () => {
  const tabs = ['winNow', 'topFactor', 'topWin'];
  const tabsContainer = useRef(null);
  const tab0 = useRef(null);
  const tab1 = useRef(null);
  const tab2 = useRef(null);
  const viewsContainer = useRef(null);
  const tabViews = useRef([]);
  const [currentTab, setCurrentTab] = useState(0);
  const isMobile = useIsMobile();
  const t = useScopedI18n('leaderboard');
  const tabLabel = useScopedI18n('leaderboard.tabs');
  const { user } = useUser();

  const currency = user?.currency || 'USD';
  const currencySymbol = user?.currencySymbol || '$';
  const { data, error, isLoading } = useSWR(
    `/leaderboard?currency=${currency}`,
    fetcherWithToken
  );
  const { gameData: dataGames } = useGameApi();

  const gamesDatas = useMemo(() => {
    if (isLoading || error || !data) return [[], [], []];

    const datas = [data.winNow || [], data.topFactor || [], data.topWin || []];

    return datas?.map((topList) => {
      return topList?.map((item) => {
        const game = dataGames?.find((g) => g?.id === item?.game);
        return {
          ...item,
          game: game,
          imageUrl: game?.imageUrl || '',
          title: game?.title || 'Unknown game',
          userName: item?.user || 'Unknown user',
        };
      });
    });
  }, [dataGames, isLoading, data, error]);

  useEffect(() => {
    const container = viewsContainer.current;
    if (!container) return;

    const onScrollEnd = () => {
      onTouchEndHandler(0);
    };
    container.addEventListener('scrollend', onScrollEnd);

    return () => {
      container.addEventListener('scrollend', onScrollEnd);
    }
  })
  if (!data) {
    return null;
  }

  const onClickHandler = (index: number) => {
    setCurrentTab(index);


    const tab = index === 0 ? tab0.current : (index === 1 ? tab1.current : tab2.current);
    const tabs = tabsContainer.current;
    const element = tabViews.current[index];
    const container = viewsContainer.current;


    if (tab && tabs) {
      tabs.scrollTo({
        left: tab.offsetLeft - 18,
        behavior: 'smooth',
      });
    }
    if (element && container) {
      container.scrollTo({
        left: element.offsetLeft,
        behavior: 'smooth',
      });
    }
  };
  const findNearestColumn = (scrollLeft) => {
    const containerWidth = viewsContainer.current.offsetWidth;
    const center = scrollLeft + containerWidth / 2;

    let minDist = Infinity;
    let targetIndex = 0;

    tabViews.current.forEach((el, index) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        const elementCenter = rect.left + rect.width / 2;
        const dist = Math.abs(elementCenter - containerWidth / 2);

        if (dist < minDist) {
          minDist = dist;
          targetIndex = index;
        }
      }
    });

    return targetIndex;
  };

  const onTouchEndHandler = (index: number) => {
    const container = viewsContainer.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const targetIndex = findNearestColumn(scrollLeft);
    onClickHandler(targetIndex);
  };

  return (
    <Wrapper>
      <Title>
        <HeadingBoldH5>{t('title')}</HeadingBoldH5>
      </Title>
      {isMobile && (
        <Tab ref={tabsContainer}>
          <TabButton
            ref = {tab0}
            $isActive={currentTab === 0}
            onClick={() => onClickHandler(0)}
          >
            {tabLabel('winNow')}
          </TabButton>
          <TabButton
            ref = {tab1}
            $isActive={currentTab === 1}
            onClick={() => onClickHandler(1)}
          >
            {tabLabel('topFactor')}
          </TabButton>
          <TabButton
            ref = {tab2}
            $isActive={currentTab === 2}
            onClick={() => onClickHandler(2)}
          >
            {tabLabel('topWin')}
          </TabButton>
        </Tab>
      )}

      <ListOfWinList ref={viewsContainer}
                     // onTouchEnd={onTouchEndHandler}
      >
        {gamesDatas &&
          gamesDatas.map((list, listIndex) => {
            return (
              <ListWrapper
                key={listIndex}
                ref={(el) => (tabViews.current[listIndex] = el)}
              >
                {!isMobile && (
                  <TabHeader>{tabLabel(tabs[listIndex])}</TabHeader>
                )}
                <WinList>
                  {list.map((item, index) => {
                    return (
                      <WinItem
                        key={index}
                        item={item}
                        currency={currency}
                        currencySymbol={currencySymbol}
                        columnIndex={listIndex}
                        columnKey={tabs[listIndex]}
                      />
                    );
                  })}
                </WinList>
              </ListWrapper>
            );
          })}
      </ListOfWinList>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 36px;
  justify-content: center;
  gap: 14px;

  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;
const Title = styled.div`
  display: flex;
  margin: 0;

  @media (max-width: 768px) {
    margin: 0 18px;
  }
`;
const Tab = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  overflow-x: scroll;
`;
const TabButton = styled.button`
  display: flex;
  height: 32px;
  padding: 0 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;

  border-radius: 8px;
  background: #27262b;

  ${({ $isActive }) =>
    $isActive &&
    `
    background: #4b444c;
  `}

  white-space: nowrap;

  &:first-child {
    margin-left: 18px;
  }

  &:last-child {
    margin-right: 18px;
  }
`;
const TabHeader = styled(BodyTextMediumA)`
  display: flex;
  height: 32px;
  //padding: 0 12px;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;

  white-space: nowrap;
`;
const ListOfWinList = styled.div`
  display: flex;
  gap: 24px;
  margin: 0;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: scroll;
    gap: 0;
    scroll-snap-type: x mandatory;
    scroll-behavior: auto;
  }
`;
const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  margin: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex: 1 0 100%;
  }
`;
const WinList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;

  @media (max-width: 768px) {
    margin: 0 18px;
  }
`;
